import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ArrowLeft, BookOpen, Mail } from 'lucide-react';
import { getContentFor, topicOptions } from '../data/verseDeliveryContent';
import { useAuth } from '../context/AuthContext';
import { createSubscription } from '../services/verseDeliveryService';
import { track } from '../services/analytics';

const returnDestinations = {
  prayer: { to: '/prayer', label: 'Today’s prayer' },
  devotional: { to: '/devotionals', label: 'Today’s devotional' },
  verse: { to: '/bible/verse-of-the-day', label: 'Today’s verse' }
};

const topicAliases = { anxiety: 'anxious', peace: 'anxious', gratitude: 'grateful', rest: 'night' };

export default function VerseDeliveryScheduler() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const requestedType = params.get('type') || 'verse';
  const returnDestination = returnDestinations[requestedType] || returnDestinations.verse;
  const carriedTopic = params.get('tags')?.split(',')[0];
  const initialTopic = topicAliases[carriedTopic] || carriedTopic;
  const [topic, setTopic] = useState(() => topicOptions.some(({ id }) => id === initialTopic) ? initialTopic : 'morning');
  const [cadence, setCadence] = useState('morning');
  const [customTime, setCustomTime] = useState('07:00');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const mounted = useRef(false);
  const entry = getContentFor(topic, 0);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const result = await createSubscription({
        topic,
        contentType: 'verse',
        cadence,
        customTime,
        timezone,
        medium: 'email',
        destination: user?.email || ''
      });
      track('rhythm_saved', { contentType: 'verse', medium: 'email' });
      // Saving can finish in the background after the reader leaves this page.
      if (mounted.current) navigate('/schedule/confirmed', { state: result });
    } catch (saveError) {
      if (!mounted.current) return;
      setError(saveError.message || 'We could not save your schedule. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="rhythm-flow">
      <header>
        <Link to={returnDestination.to}><ArrowLeft size={18} /> {returnDestination.label}</Link>
        <Link to="/settings">Settings</Link>
      </header>
      <main className="rhythm-preview">
        <p className="rhythm-eyebrow">Optional delivery</p>
        <h1>A verse for your day.</h1>
        <p>Choose when a Bible verse arrives by email. You can keep reading and praying at any time.</p>
        {requestedType === 'prayer' || requestedType === 'devotional' ? (
          <p>Scheduled delivery currently includes Bible verses. Today’s prayer and devotional are ready in the app every day.</p>
        ) : null}

        <form onSubmit={save}>
          <h2>Choose a theme</h2>
          <div className="rhythm-chips" role="group" aria-label="Verse theme">
            {topicOptions.map(({ id, label }) => (
              <button
                type="button"
                className={topic === id ? 'selected' : ''}
                aria-pressed={topic === id}
                onClick={() => setTopic(id)}
                key={id}
              >
                {label}
              </button>
            ))}
          </div>
          <section className="rhythm-hero" aria-label="Verse preview">
            <small>Your first verse</small>
            <blockquote>{entry.text}</blockquote>
            <cite>{entry.reference}</cite>
          </section>

          <section className="rhythm-settings" aria-label="Delivery settings">
            <label>
              Frequency
              <select value={cadence} onChange={(event) => setCadence(event.target.value)}>
                <option value="morning">Every day</option>
                <option value="weekdays">Weekdays</option>
                <option value="weekly">Every Sunday</option>
              </select>
            </label>
            <label>
              Delivery time
              <input type="time" value={customTime} onChange={(event) => setCustomTime(event.target.value)} required />
            </label>
            <div><span>Time zone</span><span>{timezone}</span></div>
            <div><span><Mail size={16} aria-hidden="true" /> Email to</span><span>{user?.email || 'Sign in to schedule email delivery'}</span></div>
          </section>
          {error ? <p role="alert" className="rhythm-error">{error}</p> : null}
          <p>Email delivery depends on your account’s access. Manage or pause any schedule in Settings.</p>
          <button className="rhythm-primary" type="submit" disabled={saving || !user?.email}>
            {saving ? 'Saving…' : 'Save verse schedule'}
          </button>
        </form>
        <p><Link to={returnDestination.to}><BookOpen size={18} /> Keep reading</Link></p>
      </main>
    </div>
  );
}
